import type { FC } from "react";
import type { TextInputKeyPressEvent } from "react-native";
import { View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../../theme";
import type { CountryOption } from "../phone-number";
import { CountryList } from "./CountryList";
import { CountrySearchField } from "./CountrySearchField";

interface CountryPickerDropdownProps {
  /** Distance from the top of the field to the dropdown, i.e. the field height plus a gap. */
  readonly top: number;
  readonly query: string;
  readonly onQueryChange: (text: string) => void;
  readonly onKeyPress: (e: TextInputKeyPressEvent) => void;
  readonly options: readonly CountryOption[];
  readonly selected: CountryOption["country"];
  readonly highlightedIndex: number;
  readonly onSelect: (option: CountryOption) => void;
  readonly onHighlight: (index: number) => void;
}

/** Web country picker: anchored under the field, search focused, arrow keys + Enter to pick. */
const CountryPickerDropdown: FC<CountryPickerDropdownProps> = ({
  top,
  query,
  onQueryChange,
  onKeyPress,
  options,
  selected,
  highlightedIndex,
  onSelect,
  onHighlight,
}) => (
  <View style={[styles.dropdown, { top }]}>
    <View style={styles.search}>
      <CountrySearchField value={query} onChangeText={onQueryChange} onKeyPress={onKeyPress} autoFocus />
    </View>
    <View style={styles.list}>
      <CountryList
        options={options}
        selected={selected}
        highlightedIndex={highlightedIndex}
        onSelect={onSelect}
        onHighlight={onHighlight}
      />
    </View>
  </View>
);

CountryPickerDropdown.displayName = "CountryPickerDropdown";

export { CountryPickerDropdown };

const styles = StyleSheet.create((theme) => ({
  dropdown: {
    position: "absolute",
    left: 0,
    right: 0,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing.xl,
    ...theme.shadows.sm,
    zIndex: theme.zIndex.dropdown,
    overflow: "hidden",
  },
  search: {
    padding: theme.spacing.md,
  },
  list: {
    maxHeight: 280,
  },
}));
